/* eslint-disable no-use-before-define */
import { useEffect, useLayoutEffect, useRef, useState, Suspense } from 'react'
import { useRoutes, useSearchParams, useLocation } from 'react-router-dom'
import { CssBaseline, GlobalStyles, ThemeProvider } from '@mui/material'
import { useDispatch, useSelector } from 'react-redux'
import { Client } from '@twilio/conversations'
import ChatClient from 'util/ChatClient'
import { QueryClientProvider } from '@tanstack/react-query'
import { generateCustomToken } from './apis/auth'
import { ReaderProvider } from './contexts/ReaderContext'
import { useCurrentPractice } from './contexts/PracticeContext'
import useScrollReset from './hooks/useScrollReset'
import useSettings from './hooks/useSettings'
import routes from './routes'
import { createCustomTheme } from './theme'
import SettingsDrawer from './components/SettingsDrawer'
import DelightedSurvey from './components/DelightedSurvey'
import { authClient, signInWithCustomToken } from './lib/firebase'
import { setContext, setIsAuthCheckUsingTokenLoading } from './slices/auth'
import { selectIsScratchUserLoggingIn, selectUser } from './selectors/auth'
import './App.css'
import { configureScopeWithUserId } from 'observability/sentry'
import { resetFaroUser, setFaroUser, setFaroView } from 'observability/faro'
import { queryClient } from './lib/queryClient'
import { isUnifiedDomain, validateUser } from './util/unified'
import ResourceLoading from './components/resource-loading'
import { UNIFIED_CONFIG } from './config'
import { normalizeURL } from './util/util'
import { captureCapitalAttribution } from './util/capital'
import { lazyWithRetry } from 'util/lazy-with-retry'
import { ChatWidget } from 'components/dashboard/ChatWidget'
import { ToastProvider } from './components/dashboard/ToastProvider'
import { isScratchAdminUser } from './util/user'

declare global {
	interface Window {
		dataLayer: unknown[]
	}
}

const App = () => {
	const content = useRoutes(routes)
	const { settings } = useSettings()
	const user = useSelector(selectUser)
	const isScratchUserLoggingIn = useSelector(selectIsScratchUserLoggingIn)
	const [isChatInitialized, setIsChatInitialized] = useState(false)
	const [currentChatPracitceId, setCurrentChatPracitceId] = useState<string | null>(null)
	const [chatClient, setChatClient] = useState<Client | null>(null)
	const [searchParams, setSearchParams] = useSearchParams()
	const token = searchParams.get('redirect_token')
	const [isRedirectInProgress, setIsRedirectInProgress] = useState(false)
	const dispatch = useDispatch()
	useScrollReset()
	const { currentPracticeObject } = useCurrentPractice()
	const location = useLocation()
	const baseURL = normalizeURL(UNIFIED_CONFIG.baseURL)
	const pathname = normalizeURL(location.pathname)

	// Campaign links land on the dashboard root and the login flow drops the query
	// string well before `/financing/capital`, so capture runs on mount, on
	// whatever route the app booted on.
	useEffect(() => {
		captureCapitalAttribution()
	}, [])

	useLayoutEffect(() => {
		setFaroView(location.pathname)
	}, [location.pathname])

	useEffect(() => {
		if (!token) {
			dispatch(setIsAuthCheckUsingTokenLoading(false))
		}
		if (token) {
			signInWithCustomToken(token)
				.catch((err) => {
					console.error(err)
					dispatch(setIsAuthCheckUsingTokenLoading(false))
				})
				.finally(() => {
					searchParams.delete('redirect_token')
					setSearchParams(searchParams)
				})
		}
	}, [])

	useEffect(() => {
		// check if the user/practice should be redirected
		if (!user?.uid) {
			return // If we don't have a user don't bother
		}

		// this means the redirect is already done or not needed
		if (isUnifiedDomain()) {
			return
		}
		if (isRedirectInProgress) {
			return
		}
		setIsRedirectInProgress(true)
		generateCustomToken()
			.then((res) => {
				// redirect the user to the new url
				const newURL = `${baseURL}${pathname}?redirect_token=${res.data.customToken}`
				window.location.href = newURL
			})
			.catch(console.error)
	}, [user?.uid, isRedirectInProgress])

	useEffect(() => {
		const unsubscribe = authClient().onAuthStateChanged(
			async (u) => {
				if (isScratchUserLoggingIn) {
					return
				}
				if (u) {
					const context = await validateUser(u)
					dispatch(setContext(context))
					if (context) {
						configureScopeWithUserId(u.uid, u.email)
						setFaroUser(u.uid, u.email)
					}
				} else {
					dispatch(setContext(null))
					resetFaroUser()
				}
			},
			(err) => console.error(err)
		)
		// cancel the auth state listener when the component unmounts
		return () => {
			unsubscribe()
		}
	}, [isScratchUserLoggingIn])

	const theme = createCustomTheme({
		compact: settings.compact,
		direction: settings.direction,
		responsiveFontSizes: settings.responsiveFontSizes,
		roundedCorners: settings.roundedCorners,
		theme: settings.theme
	})

	const useStyles = (
		<GlobalStyles
			styles={{
				'*': {
					boxSizing: 'border-box',
					margin: 0,
					padding: 0
				},
				html: {
					'-webkit-font-smoothing': 'antialiased',
					'-moz-osx-font-smoothing': 'grayscale',
					height: '100%',
					width: '100%'
				},
				body: {
					height: '100%',
					width: '100%',
					fontSize: 14
				},
				'#root': {
					height: '100%',
					width: '100%'
				},
				a: {
					color: '#5B70FF'
				},
				'.grecaptcha-badge': {
					visibility: 'hidden'
				}
			}}
		/>
	)

	useEffect(() => {
		// Add event and data (userId) to data layer for Google tag manager variables
		if (user && Array.isArray(window.dataLayer)) {
			window.dataLayer.push({ event: 'userLoaded', userId: user.uid, userEmail: user.email })
		}
	}, [user])

	const lastPracticeLoadedPush = useRef<string>()

	useEffect(() => {
		const hasPracticeIdentifier =
			!!currentPracticeObject?.stripeAccountId || !!currentPracticeObject?.consolidatedPractice?.id
		if (!user?.uid || !hasPracticeIdentifier || !Array.isArray(window.dataLayer)) {
			return
		}

		const payload = {
			event: 'practiceLoaded',
			userId: user.uid,
			consolidatorId: currentPracticeObject.consolidatorId ?? '',
			currentPims: currentPracticeObject.currentPims ?? '',
			clinicState: currentPracticeObject.clinicState ?? '',
			practiceId: currentPracticeObject.stripeAccountId ?? '',
			consolidatedPracticeId: currentPracticeObject.consolidatedPractice?.id ?? ''
		}

		// currentPracticeObject is rebuilt on every context refresh, so compare values rather than
		// the object reference to avoid re-identifying Stonly with data it already has.
		const payloadKey = JSON.stringify(payload)
		if (lastPracticeLoadedPush.current === payloadKey) {
			return
		}
		lastPracticeLoadedPush.current = payloadKey

		window.dataLayer.push(payload)
	}, [user, currentPracticeObject])

	// Fallback usage since makeStyles isn't working
	document.body.dataset.theme = settings.theme

	useEffect(() => {
		if (isChatInitialized && currentPracticeObject?.id === currentChatPracitceId) {
			return
		}

		if (currentPracticeObject?.isChatEnabled && user && currentPracticeObject) {
			if (isChatInitialized && currentPracticeObject.id !== currentChatPracitceId) {
				const fetch = async () => {
					chatClient?.removeAllListeners()
				}
				fetch().catch(console.error)
			}

			// const practiceToken = await getToken(currentPracticeObject?.stripeAccountId);
			const fetch = async () => {
				try {
					const { initPracticeConvos } = await import('./util/twilio-helpers')
					const twilioClient = await initPracticeConvos(currentPracticeObject, user, dispatch)
					ChatClient.chatSDK = twilioClient
					setChatClient(twilioClient)
					setIsChatInitialized(true)
					setCurrentChatPracitceId(currentPracticeObject.id as string) // TODO: cuid?
				} catch (err) {
					console.error('Failed to initialize chat')
				}
			}
			fetch().catch(console.error)
		}
	}, [user, currentPracticeObject])

	const ContactSupport = lazyWithRetry(() => import('./pages/errors/ContactSupport'))
	useEffect(() => {
		const init = async () => {
			import('./util/init-aggrid').then((module) => module.default())
		}
		init().catch(console.error)
	}, [])

	const shouldContactSupport = () => {
		const hasPrimaryClinic = user.primaryClinic
		const isLending = user.financingStatus === 'active' // Lending users don't have a primary clinic
		const hasPractices =
			user.practices && Array.isArray(user.practices) && user.practices.length >= 1

		if ((!hasPrimaryClinic && !isLending) || (!hasPractices && !isScratchAdminUser(user))) {
			return true
		}

		return false
	}

	return (
		<QueryClientProvider client={queryClient}>
			<ThemeProvider theme={theme}>
				<ToastProvider />
				<ReaderProvider>
					<CssBaseline />
					{useStyles}
					<SettingsDrawer />
					<ChatWidget />
					<Suspense fallback={<ResourceLoading />}>
						{user && shouldContactSupport() ? <ContactSupport /> : content}
					</Suspense>
					<DelightedSurvey />
				</ReaderProvider>
			</ThemeProvider>
		</QueryClientProvider>
	)
}

export default App
